/**
 * LoD2 view of a building and its surroundings.
 *
 * The surfaces arrive ready from the server (`/city-objects/renderable`):
 * dequantised, projected to WGS84 and tagged with their semantic type. Nothing
 * is reconstructed here — the previous 3D path extruded the footprint into a
 * prism, which gave every building a flat roof even though the real roof planes
 * were in the data all along. A prism looks plausible, and that is exactly why
 * it went unnoticed for two years.
 *
 * Where the data is missing the view says so. An empty map and a map of a place
 * without LoD2 coverage look identical otherwise.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as THREE from "three";
import mapboxgl from "mapbox-gl";
import { Threebox } from "threebox-plugin";
import earcut from "earcut";

import { useAtomValue } from "jotai";
import { booleanPointInPolygon } from "@turf/turf";
import { Maximize2, Minimize2, Pin, PinOff } from "lucide-react";
import { authenticationAtom } from "../../State";
import { userAPI } from "../../UserAPI";
import { MAPBOX_TOKEN } from '../../utilities/mapbox';
import { buildVegetationGroup, type LawnArea, type VegetationItem } from "./vegetation3d";
import Building3DControls from "./Building3DControls";
import { loadParcelsForArea } from "../../heatmap/ParcelsAPI";
import { circleGeometry } from "../../heatmap/areaGeometry";
import { applyAppearance, hasPartClasses, type ColourMode } from "./modelAppearance";
import MassingPanel from "./MassingPanel";
import type { DrawMode } from "./Building3DControls";
import {
  DEFAULT_STOREYS,
  DEFAULT_STOREY_HEIGHT_M,
  bodiesGeoJson,
  centroidOf,
  closed,
  draftGeoJson,
  newBodyId,
  outlinesGeoJson,
  rectangleFrom,
  rightAngleCorners,
  rotateRing,
  setbackDepthM,
  setbackOf,
  setbackOutside,
  setbackRuleFor,
  snapPolygonPoint,
  translateRing,
  footprintsFromSurfaces,
  openSpaceOf,
  type LonLat,
  type MassingBody,
  type MassingContext,
} from "./massing";

/**
 * A floating label the map should carry. Supplied by the caller, so the view
 * stays a renderer: it does not decide what deserves a name. The server can
 * later put the same shape into the map data without this interface changing.
 */
export interface Lod2Label {
  text: string;
  lon: number;
  lat: number;
  /** Height above the roof in metres; falls back to `DEFAULT_LABEL_OFFSET`. */
  offsetMetres?: number;
}

interface Properties {
  building: any;
  /** Metres around the building centre. */
  radius?: number;
  /** Labels to float above the scene. */
  labels?: Lod2Label[];
}

/** Far enough above a ridge that the text does not touch it, close enough that
    the tether still reads as belonging to that building. */
export const DEFAULT_LABEL_OFFSET = 10;

/** Label height as a fraction of the viewport. Large enough to read at a
    glance, small enough that it does not cover the building it names. */
const LABEL_SCREEN_HEIGHT = 0.03;

interface Surface {
  type?: string;
  rings: number[][][];
}

interface RenderableBuilding {
  id: string;
  /** The building record this geometry belongs to — an own model replaces exactly this one. */
  buildingId?: string;
  measuredHeight?: number;
  roofType?: string;
  center: number[];
  surfaces: Surface[];
}

/** Roof, wall and ground in distinguishable colours — the most visible gain
    over the single-coloured block the old path produced. Picked as sRGB and converted to linear
    before use (`surfaceColour`): Threebox renders with sRGB output, and unconverted the walls burnt
    out to white and the roofs washed out to salmon, with no light and shade left on the facades. */
const SURFACE_COLOURS: Record<string, number> = {
  RoofSurface: 0x9a5a48,
  WallSurface: 0xd2cbbf,
  GroundSurface: 0x8a8a8a,
};
const DEFAULT_COLOUR = 0xc2bcb2;
const surfaceColour = (type?: string) => new THREE.Color(SURFACE_COLOURS[type ?? ""] ?? DEFAULT_COLOUR).convertSRGBToLinear();
/** Shadows on the ground: light and neutral-warm. A cool blue read as cold and dark next to the
    warm facades (measured by eye, 24.10.2026). */
const SHADOW_COLOUR = 0x3b342c;
const SHADOW_OPACITY = 0.14;
/** Shadow map resolution; with the frustum fitted to the scene this is ~10 px per metre. */
const SHADOW_MAP_SIZE = 4096;

/**
 * Pure: half the side of the sun's shadow frustum in scene units. Threebox gives it a fixed ±1000,
 * so the scene used a sliver of the shadow map and the edges came out frayed. The scene is a
 * scaled copy of the world (Threebox rescales it with the zoom), so the fitted size is
 * metres → world units → scene units, recomputed per frame.
 */
export const shadowFrustumHalfSize = (radiusMetres: number, unitsPerMetre: number, worldScale: number): number =>
  Math.max(radiusMetres, 50) * unitsPerMetre * Math.abs(worldScale || 1);

/**
 * Threebox's own `projectToWorld`, reproduced.
 *
 * The library keeps `utils` module-private — only `Threebox` and `THREE` are
 * exported — so `tb.utils` is undefined and reaching for it throws, which is
 * why the houses vanished entirely. The constants and the formula are taken
 * verbatim from `src/utils/{constants,utils}.js` so the result agrees with the
 * camera the library sets up.
 */
const WORLD_SIZE = 1024000;
/** Threebox's distance from the scene to the sun (`setSunlight`: WORLD_SIZE / 2). */
const SUN_DISTANCE = WORLD_SIZE / 2;
const EARTH_RADIUS = 6371008.8;
const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS;
const PROJECTION_WORLD_SIZE = WORLD_SIZE / (EARTH_RADIUS * Math.PI * 2);
const DEG2RAD = Math.PI / 180;

/** World units per metre at a latitude — the same factor `projectToWorld` applies to heights. */
const unitsPerMetreAt = (lat: number): number =>
  Math.abs(WORLD_SIZE / Math.cos(DEG2RAD * lat) / EARTH_CIRCUMFERENCE);

/** Radius of the vegetation analysis. Smaller than the building radius: the analysis takes minutes
    and grows with the area, and what matters for the picture is the green next to the building. */
const VEGETATION_RADIUS = 150;
/** While an analysis runs, the view asks this often whether it is done. */
const VEGETATION_POLL_MS = 8000;

interface VegetationState {
  status: "none" | "pending" | "ready" | "failed";
  items?: VegetationItem[];
  lawn?: LawnArea[];
  heightKnown?: boolean;
  estimatedCount?: number;
}

/** An own 3D model of the building (tri-server docs/building-models.md). */
export interface ModelPlacement {
  lon: number;
  lat: number;
  altitude: number;
  rotationDeg: number;
  source?: string;
}
interface BuildingModel {
  _id: string;
  name: string;
  format: string;
  glbFileId: string | null;
  placement: ModelPlacement | null;
  georeferenceIgnored: { distanceM: number } | null;
  /** The user's choice, else the default the server derived on upload. */
  colourMode?: ColourMode;
}

/** Metres → degrees at a latitude, for the nudge buttons. */
const METRES_PER_DEGREE = 111320;
/**
 * Threebox's `setRotation` treats 0 as "keep the current angle" (`radify(0) || rotation.z`), so
 * turning a model back to exactly 0° did nothing. A thousandth of a degree is invisible and is an
 * angle.
 */
export const threeboxDegrees = (deg: number): number => (deg === 0 ? 0.001 : deg);

/**
 * Shadows: the sun for a chosen day and hour (Threebox's `realSunlight` + SunCalc). The default is a
 * summer afternoon — "now" would be dark half the day, and a shadow study starts in June.
 */
export const defaultSunDate = (now: Date = new Date()): Date => new Date(now.getFullYear(), 5, 21, 15, 0, 0);
/** Pure: the same calendar day at another hour (fractions are minutes), in the viewer's time zone. */
export const atHour = (day: Date, hour: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(hour), Math.round((hour % 1) * 60), 0);
/** Pure: "2026-06-21" from a date, local time — what an `<input type="date">` wants. */
export const isoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const PARCELS_KEY = "building3D.parcels";
const readParcelsPreference = (): boolean => {
  try {
    return window.localStorage.getItem(PARCELS_KEY) === "1";
  } catch {
    return false;
  }
};
/** Flurstücke am Boden: Mapbox-Ebenen unter der three.js-Szene, damit die Gebäude darüber stehen. */
const PARCEL_LAYERS = ["parcels-fill", "parcels-line", "parcels-label"];
const SHADOWS_KEY = "building3D.shadows";
const readShadowsPreference = (): boolean => {
  try {
    return window.localStorage.getItem(SHADOWS_KEY) === "1";
  } catch {
    return false;
  }
};

export const nudge = (p: ModelPlacement, east: number, north: number): ModelPlacement => ({
  ...p,
  lat: p.lat + north / METRES_PER_DEGREE,
  lon: p.lon + east / (METRES_PER_DEGREE * Math.cos((p.lat * Math.PI) / 180)),
});

const projectToWorld = (lon: number, lat: number, altitude: number): number[] => {
  const x = -EARTH_RADIUS * DEG2RAD * lon * PROJECTION_WORLD_SIZE;
  const y =
    -EARTH_RADIUS *
    Math.log(Math.tan(Math.PI * 0.25 + 0.5 * DEG2RAD * lat)) *
    PROJECTION_WORLD_SIZE;
  const unitsPerMetre = Math.abs(WORLD_SIZE / Math.cos(DEG2RAD * lat) / EARTH_CIRCUMFERENCE);
  return [x, y, altitude * unitsPerMetre];
};

/**
 * Triangulate one planar ring set. A sloped roof cannot be triangulated in the
 * xy plane — its projection degenerates — so the surface is rotated into its
 * own plane first, triangulated there, and the resulting indices applied to the
 * original 3D positions.
 */
const triangulate = (positions: number[][]): number[] => {
  if (positions.length < 3) return [];

  // Newell's method: a normal that is stable for non-convex and slightly
  // non-planar rings, which real roof surfaces frequently are.
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < positions.length; i += 1) {
    const current = positions[i];
    const next = positions[(i + 1) % positions.length];
    nx += (current[1] - next[1]) * (current[2] + next[2]);
    ny += (current[2] - next[2]) * (current[0] + next[0]);
    nz += (current[0] - next[0]) * (current[1] + next[1]);
  }
  const length = Math.hypot(nx, ny, nz);
  if (length === 0) return [];
  nx /= length;
  ny /= length;
  nz /= length;

  // Any two axes orthogonal to the normal give a basis of the surface's plane.
  const reference = Math.abs(nz) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  let ux = reference[1] * nz - reference[2] * ny;
  let uy = reference[2] * nx - reference[0] * nz;
  let uz = reference[0] * ny - reference[1] * nx;
  const uLength = Math.hypot(ux, uy, uz);
  if (uLength === 0) return [];
  ux /= uLength;
  uy /= uLength;
  uz /= uLength;
  const vx = ny * uz - nz * uy;
  const vy = nz * ux - nx * uz;
  const vz = nx * uy - ny * ux;

  const flat: number[] = [];
  positions.forEach((position) => {
    flat.push(
      position[0] * ux + position[1] * uy + position[2] * uz,
      position[0] * vx + position[1] * vy + position[2] * vz,
    );
  });

  return earcut(flat);
};

/**
 * A floating label: text that keeps its size on screen, always faces the camera,
 * and is tethered to the roof it belongs to.
 *
 * A `THREE.Sprite` with `sizeAttenuation: false` is measured in screen space
 * rather than world space, so the text stays legible at every zoom instead of
 * shrinking to a speck over the town. The tether matters as much as the text:
 * a label hanging free in the air belongs to no particular building, and with
 * neighbours around that ambiguity is the whole problem.
 *
 * Returns the objects to add; the caller owns them.
 */
const createLabel = (
  text: string,
  lon: number,
  lat: number,
  roofAltitude: number,
  offsetMetres: number,
): any[] => {
  const FONT = 44;
  const PADDING = 18;
  // The portal's body font (index.css). Canvas takes a CSS font string, so the
  // same stack applies — including the fallbacks, for the moment before a web
  // font has loaded.
  const FONT_STACK = `600 ${FONT}px "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

  const measure = document.createElement("canvas").getContext("2d");
  if (!measure) return [];
  measure.font = FONT_STACK;

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(measure.measureText(text).width) + PADDING * 2;
  canvas.height = FONT + PADDING * 2;

  const context = canvas.getContext("2d");
  if (!context) return [];
  context.font = FONT_STACK;
  context.fillStyle = "rgba(255, 255, 255, 0.92)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  // Neutral, not branded: the label names a building, it does not mark a state.
  // Colour here would compete with the roof/wall/ground colouring, which does
  // carry meaning.
  context.strokeStyle = "#8a8886";
  context.lineWidth = 2;
  context.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);
  context.fillStyle = "#323130";
  context.textBaseline = "middle";
  context.fillText(text, PADDING, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      // Screen space, not world space — that is what keeps the size constant.
      sizeAttenuation: false,
      // A label hidden behind the roof it names would be worse than none.
      depthTest: false,
      depthWrite: false,
    }),
  );

  // With `sizeAttenuation: false` the scale is a fraction of the viewport — but
  // only if no ancestor scales it. Threebox writes a transform straight into
  // `world.matrix` (its decomposed `scale` stays 1, which is what makes this
  // hard to see), and that matrix carries a factor of roughly 130. The base
  // size is therefore remembered here and divided by the live world scale on
  // every frame; see `rescaleLabels`.
  const height = LABEL_SCREEN_HEIGHT;
  const width = height * (canvas.width / canvas.height);
  sprite.scale.set(width, height, 1);
  sprite.userData.baseScale = { width, height };
  sprite.renderOrder = 1000;

  const top = projectToWorld(lon, lat, roofAltitude + offsetMetres);
  const foot = projectToWorld(lon, lat, roofAltitude);
  sprite.position.set(top[0], top[1], top[2]);

  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(foot[0], foot[1], foot[2]),
      new THREE.Vector3(top[0], top[1], top[2]),
    ]),
    new THREE.LineBasicMaterial({ color: 0x8a8886, depthTest: false }),
  );
  line.renderOrder = 999;

  return [line, sprite];
};


/**
 * Undo the world transform's scale on label sprites.
 *
 * Called per frame because Threebox rewrites `world.matrix` as the map moves.
 * Without this the text grows with the zoom instead of staying put — the exact
 * opposite of what a screen-space label is for.
 */
const rescaleLabels = (world: any) => {
  const factor = Math.abs(world?.matrixWorld?.elements?.[0] ?? 1) || 1;
  world.traverse((object: any) => {
    const base = object?.userData?.baseScale;
    if (!base) return;
    object.scale.set(base.width / factor, base.height / factor, 1);
  });
};

export default function Building3DView(properties: Properties) {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const [buildings, setBuildings] = useState<RenderableBuilding[] | null>(null);
  const [status, setStatus] = useState<{ total: number; rendered: number; truncated: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const authentication: any = useAtomValue(authenticationAtom);
  const threeboxRef = useRef<any>(null);
  const modelObjectRef = useRef<any>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [vegetation, setVegetation] = useState<VegetationState | null>(null);
  const [vegetationStarting, setVegetationStarting] = useState(false);
  const [shadows, setShadows] = useState<boolean>(readShadowsPreference);
  const [parcelsOn, setParcelsOn] = useState<boolean>(readParcelsPreference);
  const [parcels, setParcels] = useState<{ type: "FeatureCollection"; features: any[] } | null>(null);
  const [parcelsLoading, setParcelsLoading] = useState(false);
  const [sunDate, setSunDate] = useState<Date>(() => defaultSunDate());
  const sunDateRef = useRef<Date>(sunDate);
  sunDateRef.current = sunDate;
  const [models, setModels] = useState<BuildingModel[]>([]);
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [draft, setDraft] = useState<ModelPlacement | null>(null);
  const [savedPlacement, setSavedPlacement] = useState<ModelPlacement | null>(null);
  const [modelBusy, setModelBusy] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const activeModel = models[0] ?? null;
  const [colourMode, setColourMode] = useState<ColourMode>("original");
  const colourModeRef = useRef<ColourMode>("original");
  colourModeRef.current = colourMode;
  const [colourModesAvailable, setColourModesAvailable] = useState(false);
  const hasModel = !!activeModel;
  const hasDraft = !!draft;

  const buildingCentre = useMemo(() => {
    const b = properties.building;
    if (!b) return null;
    if (b.minLat != null && b.maxLat != null && b.minLon != null && b.maxLon != null) {
      return { lat: 0.5 * (b.minLat + b.maxLat), lon: 0.5 * (b.minLon + b.maxLon) };
    }
    if (b.lat != null && b.lon != null) return { lat: b.lat, lon: b.lon };
    return null;
  }, [properties.building]);
  /**
   * Where the server says the building stands. A tenant building without LoD2 id has a geocoded
   * position and the nearest OSM footprint — for "Siegesstraße 106-108" the hall at No. 122; the
   * server then takes place and cubature from the public buildings at the address (`centre`).
   */
  const [serverCentre, setServerCentre] = useState<{ lat: number; lon: number } | null>(null);
  const centre = serverCentre ?? buildingCentre;

  useEffect(() => {
    if (!buildingCentre) {
      setBuildings(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        setFailed(false);
        setBuildings(null);
        // `handleAuthenticatedResponse` returns `unknown` — deliberately, since
        // it cannot know the shape. The endpoint's contract is documented on the
        // server side; naming it here would be a second, drifting copy.
        const result: any = await userAPI.getBuildingsWith3D(
          String(properties.building._id),
          properties.radius ?? 300,
        );
        if (cancelled) return;

        // The geometry rides on the building objects; buildings without LoD2
        // coverage carry `geometry3D: null` and are simply not drawn.
        const all = Array.isArray(result?.buildings) ? result.buildings : [];
        const placed = result?.centre;
        setServerCentre(
          Array.isArray(result?.counterpartIds) && result.counterpartIds.length > 0 &&
            Number.isFinite(placed?.lat) && Number.isFinite(placed?.lon)
            ? { lat: placed.lat, lon: placed.lon }
            : null,
        );
        setBuildings(
          all
            .filter((entry: any) => entry.geometry3D)
            .map((entry: any) => ({ ...entry.geometry3D, buildingId: String(entry._id) })),
        );
        setStatus({
          total: all.length,
          rendered: result?.withGeometry ?? 0,
          truncated: (result?.failedTiles ?? []).length > 0,
        });
      } catch (error) {
        if (!cancelled) {
          setFailed(true);
          setBuildings([]);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildingCentre, properties.building._id, properties.radius]);

  useEffect(() => {
    if (!mapContainerRef.current || !centre || !buildings || buildings.length === 0) return;

    let aborted = false;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [centre.lon, centre.lat],
      zoom: 18,
      pitch: 45,
      bearing: 0,
      antialias: true,
    });
    mapRef.current = map;

    // Threebox draws through Mapbox's CustomLayerInterface — the scene is only
    // painted from the layer's `render` callback. Constructing a Threebox
    // without adding such a layer builds every mesh correctly and shows none of
    // them, which is indistinguishable from having no data.
    // `style.load`, not `load`. The latter waits for the map to reach a fully
    // idle state and was measured never firing here, leaving an empty grey
    // canvas; `style.load` is the documented point at which layers may be added
    // and fires reliably.
    map.on("style.load", () => {
      if (aborted) return;
      // Threebox reaches for a global `tb` from inside its own object code, so
      // the global has to exist. It is also why the scene once drifted against
      // the map: whichever instance was assigned last owns it. We keep our own
      // reference and make it current before anything touches Threebox.
      let threebox: any = null;
      const makeCurrent = () => {
        (window as any).tb = threebox;
        return threebox;
      };

      /**
       * The map's own building footprints come from OpenStreetMap and sit
       * beside the official cubature we draw. Two sources for the same object
       * invite the reader to trust the wrong one, so the base map keeps the
       * ground and the streets and we supply the buildings.
       */
      map.getStyle()?.layers?.forEach((layer: any) => {
        if (/building/i.test(layer.id) || layer.type === "fill-extrusion") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      });

      map.addLayer({
        id: "lod2-buildings",
        type: "custom",
        renderingMode: "3d",
        onAdd(_map: any, gl: any) {
          if (aborted) return;
          // Selecting and dragging are for an own model only — the LoD2 hulls and the vegetation
          // are plain meshes, not Threebox objects, so they cannot be picked up by accident.
          threebox = new Threebox(map, gl, {
            defaultLights: true,
            enableSelectingObjects: true,
            enableDraggingObjects: true,
            enableRotatingObjects: true,
            // The sun with shadows, only when asked for: an extra pass over the whole scene.
            realSunlight: shadows,
          });
          makeCurrent();
          threeboxRef.current = threebox;

          // Less fill light than before, and a filmic tone curve: with sRGB output and four lights
          // the facades clipped to white. The sun (when shadows are on) brings its own light.
          threebox.lights.ambientLight = new THREE.AmbientLight(
            new THREE.Color("hsl(0, 0%, 100%)"),
            shadows ? 0.35 : 0.55,
          );
          threebox.scene.add(threebox.lights.ambientLight);
          threebox.renderer.toneMapping = THREE.ACESFilmicToneMapping;
          threebox.renderer.toneMappingExposure = 1.15;

      /**
       * Vertices go in as absolute lon/lat/height and Threebox projects them —
       * no local metres, no anchor, no `Object3D`.
       *
       * The previous route built each house in metres around its own centre and
       * placed it with `setCoords`. The anchors were provably right: a Mapbox
       * dot on the very same coordinate lands inside the grey footprint, while
       * the cubature did not. So the fault was in the placement, and every
       * quantity involved in it — the metres-per-degree conversion, Threebox's
       * unit scaling, its auto-centring on the bounding box — is removed here
       * rather than corrected. `projectToWorld` is the one conversion the
       * library guarantees to agree with its own camera.
       */
      buildings.forEach((building) => {
        if (!building.surfaces || !Array.isArray(building.surfaces)) return;
        // An own model replaces the LoD2 hull of this building — two hulls on top of each other
        // would make the reader guess which one is meant. Only hide if the model has placement.
        if (hasModel && draft && building.buildingId === String(properties.building._id)) return;
        
        // Heights are absolute NHN (around 480 m in Bavaria); the building is
        // put on the ground by subtracting its own lowest point.
        const groundLevel = Math.min(
          ...building.surfaces.flatMap((surface) =>
            (surface.rings[0] ?? []).map((vertex) => vertex[2]),
          ),
        );

        building.surfaces.forEach((surface) => {
          // Only the outer ring is rendered. Holes exist in the data but are
          // rare in LoD2 roofs, and earcut needs the hole indices threaded
          // through — that is a separate step, not a silent approximation.
          const ring = surface.rings[0];
          if (!ring || ring.length < 3) return;

          const world = ring.map((vertex) =>
            projectToWorld(vertex[0], vertex[1], vertex[2] - groundLevel),
          );

          const indices = triangulate(world);
          if (indices.length === 0) return;

          const positions = new Float32Array(world.length * 3);
          world.forEach((vertex, index) => {
            positions[index * 3] = vertex[0];
            positions[index * 3 + 1] = vertex[1];
            positions[index * 3 + 2] = vertex[2];
          });

          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
          geometry.setIndex(indices);
          geometry.computeVertexNormals();

          const material = new THREE.MeshLambertMaterial({
            color: surfaceColour(surface.type),
            side: THREE.DoubleSide,
          });

          // Into `world`, not `scene`: `tb.add()` puts objects in that group
          // and the camera is set up against it. A mesh parked directly on the
          // scene is outside that frame.
          const mesh = new THREE.Mesh(geometry, material);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          threebox.world.add(mesh);
        });
      });

          /**
           * Labels last, so the roof heights are known. Each one is placed over
           * the building it names — matched by proximity to that building's
           * centre — and falls back to ground level where no cubature was
           * loaded, which is still better than no label at all.
           */
          (properties.labels ?? []).forEach((label) => {
            let roofAltitude = 0;
            let nearest = Infinity;

            buildings.forEach((building) => {
              const distance = Math.hypot(
                building.center[0] - label.lon,
                building.center[1] - label.lat,
              );
              if (distance >= nearest) return;
              nearest = distance;

              const heights = building.surfaces.flatMap((surface) =>
                (surface.rings[0] ?? []).map((vertex) => vertex[2]),
              );
              roofAltitude = heights.length
                ? Math.max(...heights) - Math.min(...heights)
                : 0;
            });

            createLabel(
              label.text,
              label.lon,
              label.lat,
              roofAltitude,
              label.offsetMetres ?? DEFAULT_LABEL_OFFSET,
            ).forEach((object) => threebox.world.add(object));
          });

          if (shadows) {
            threebox.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            const sun = threebox.lights.dirLight;
            if (sun) {
              sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
              sun.shadow.map?.dispose?.();
              sun.shadow.map = null;
              // Threebox puts the sun WORLD_SIZE/2 (512,000 units) from the scene and lets its shadow
              // camera reach 400 million units deep. A depth bias is relative to that range: -0.0005
              // shifted the shadows by ~200,000 units — off the scene, all shadows gone. Instead the
              // depth range is narrowed around the sun's distance, which also sharpens the shadows;
              // no bias.
              sun.shadow.camera.near = SUN_DISTANCE * 0.5;
              sun.shadow.camera.far = SUN_DISTANCE * 1.5;
              sun.shadow.camera.updateProjectionMatrix();
            }
            // realSunlight brings a sky light tuned to a dark, saturated blue; in every spot the sun
            // does not reach it tinted facades and shadows cold and dark. Neutral, slightly warm.
            if (threebox.lights.hemiLight) {
              threebox.lights.hemiLight.color.set(0xf2f0ea);
              threebox.lights.hemiLight.groundColor.set(0xb9ab95);
            }
            // The base map cannot receive three.js shadows, so a transparent ground takes them:
            // a quad around the centre that shows nothing but the shadow.
            const reach = (properties.radius ?? 300) + 100;
            const dLat = reach / 111320;
            const dLon = reach / (111320 * Math.cos((centre.lat * Math.PI) / 180));
            const corners = [
              projectToWorld(centre.lon - dLon, centre.lat - dLat, 0.02),
              projectToWorld(centre.lon + dLon, centre.lat - dLat, 0.02),
              projectToWorld(centre.lon + dLon, centre.lat + dLat, 0.02),
              projectToWorld(centre.lon - dLon, centre.lat + dLat, 0.02),
            ];
            const ground = new THREE.BufferGeometry();
            ground.setAttribute("position", new THREE.BufferAttribute(new Float32Array(corners.flat()), 3));
            ground.setIndex([0, 1, 2, 0, 2, 3]);
            ground.computeVertexNormals();
            const groundMesh = new THREE.Mesh(ground, new THREE.ShadowMaterial({ color: SHADOW_COLOUR, opacity: SHADOW_OPACITY, side: THREE.DoubleSide }));
            groundMesh.receiveShadow = true;
            threebox.world.add(groundMesh);
            const setSun = () => threebox.setSunlight(sunDateRef.current, [centre.lon, centre.lat]);
            setSun();
            // Threebox resets the sun to "now" on the map's first idle — set ours after it.
            map.once("idle", () => {
              setSun();
              map.triggerRepaint();
            });
          }
          threebox.update?.();
          setSceneReady(true);
        },
        render() {
          const current = makeCurrent();
          if (current) {
            rescaleLabels(current.world);
            if (shadows && current.lights?.dirLight && centre) {
              // Fit the shadow frustum to the scene — Threebox's fixed ±1000 left it frayed.
              const half = shadowFrustumHalfSize(
                (properties.radius ?? 300) + 100,
                unitsPerMetreAt(centre.lat),
                current.world?.matrixWorld?.elements?.[0] ?? 1,
              );
              // …and aim it at our scene, not at the scene origin: the tight frustum would
              // otherwise miss the buildings once the map is panned away from it.
              const sunLight = current.lights.dirLight;
              const [cx, cy, cz] = projectToWorld(centre.lon, centre.lat, 0);
              const target = new THREE.Vector3(cx, cy, cz).applyMatrix4(current.world.matrixWorld);
              if (!sunLight.target.parent) current.scene?.add(sunLight.target);
              sunLight.target.position.copy(target);
              sunLight.target.updateMatrixWorld();
              const camera = sunLight.shadow.camera;
              if (Math.abs(camera.right - half) > half * 0.01) {
                camera.left = camera.bottom = -half;
                camera.right = camera.top = half;
                camera.updateProjectionMatrix();
              }
            }
            current.update();
          }
        },
      } as any);
    });

    return () => {
      aborted = true;
      threeboxRef.current = null;
      modelObjectRef.current = null;
      setSceneReady(false);
      map.remove();
      mapRef.current = null;
    };
  }, [buildings, centre, properties.labels, hasModel, hasDraft, properties.building._id, shadows]);

  // A new day or hour moves the sun in the running scene; no rebuild.
  useEffect(() => {
    const threebox = threeboxRef.current;
    if (!shadows || !sceneReady || !threebox || !centre) return;
    threebox.setSunlight(sunDate, [centre.lon, centre.lat]);
    mapRef.current?.triggerRepaint?.();
  }, [shadows, sceneReady, sunDate, centre]);

  // Die Flurstücke im Umkreis der Gebäude, einmal geladen, wenn der Schalter das erste Mal an ist.
  useEffect(() => {
    if (!parcelsOn || parcels || !centre) return;
    let cancelled = false;
    setParcelsLoading(true);
    loadParcelsForArea(circleGeometry(centre.lat, centre.lon, (properties.radius ?? 300) / 1000), centre)
      .then((result) => {
        if (cancelled) return;
        setParcels({
          type: "FeatureCollection",
          features: result.parcels
            .filter((parcel: any) => parcel?.geometry?.type)
            .map((parcel: any) => ({
              type: "Feature",
              geometry: parcel.geometry,
              properties: { number: (typeof parcel.getParcelNumber === "function" ? parcel.getParcelNumber() : null) ?? "" },
            })),
        });
      })
      .catch(() => {
        if (!cancelled) setParcels({ type: "FeatureCollection", features: [] });
      })
      .finally(() => {
        if (!cancelled) setParcelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parcelsOn, parcels, centre, properties.radius]);

  // Auf die Karte, sobald die Szene steht — nach jedem Neuaufbau der Karte erneut —, unter die Gebäude.
  useEffect(() => {
    const map = mapRef.current;
    if (!sceneReady || !map || !parcels) return;
    if (!map.getSource("parcels")) {
      map.addSource("parcels", { type: "geojson", data: parcels });
      const below = map.getLayer("lod2-buildings") ? "lod2-buildings" : undefined;
      map.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#7B61D1", "fill-opacity": 0.08 } }, below);
      map.addLayer({ id: "parcels-line", type: "line", source: "parcels", paint: { "line-color": "#5A3FB5", "line-width": 1.4, "line-opacity": 0.9 } }, below);
      map.addLayer(
        {
          id: "parcels-label",
          type: "symbol",
          source: "parcels",
          minzoom: 17,
          layout: { "text-field": ["get", "number"], "text-size": 11, "symbol-placement": "point" },
          paint: { "text-color": "#3B2A80", "text-halo-color": "#ffffff", "text-halo-width": 1.2 },
        },
        below,
      );
    }
    PARCEL_LAYERS.forEach((id) => map.getLayer(id) && map.setLayoutProperty(id, "visibility", parcelsOn ? "visible" : "none"));
    map.triggerRepaint?.();
  }, [sceneReady, parcels, parcelsOn]);

  // ── Volumenstudie ──────────────────────────────────────────────────────────────────────────────
  const [massingBodies, setMassingBodies] = useState<MassingBody[]>([]);
  const [massingSaved, setMassingSaved] = useState<string>("[]");
  const [massingSelected, setMassingSelected] = useState<string | null>(null);
  const [massingMode, setMassingMode] = useState<DrawMode>(null);
  const [massingPoints, setMassingPoints] = useState<LonLat[]>([]);
  const [massingHover, setMassingHover] = useState<LonLat | null>(null);
  const [massingContext, setMassingContext] = useState<MassingContext | null>(null);
  const [massingContextLoading, setMassingContextLoading] = useState(false);
  const [massingSaving, setMassingSaving] = useState(false);
  const [massingError, setMassingError] = useState<string | null>(null);
  const massingPointsRef = useRef<LonLat[]>([]);
  massingPointsRef.current = massingPoints;
  const [snapRightAngles, setSnapRightAngles] = useState(true);
  const [massingSnapped, setMassingSnapped] = useState(false);
  const snapRef = useRef(true);
  snapRef.current = snapRightAngles;
  // Die Palette fliegt ein, wenn die Maus an den rechten Rand der Karte kommt, und geht, wenn sie sie verlässt —
  // es sei denn, sie ist angeheftet (Knopf, gemerkt).
  const [palettePinned, setPalettePinned] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem("building3D.palettePinned") === "1";
    } catch {
      return false;
    }
  });
  const [paletteHover, setPaletteHover] = useState(false);
  const paletteOpen = palettePinned || paletteHover;
  const togglePalettePin = () => {
    setPalettePinned((pinned) => {
      try {
        window.localStorage.setItem("building3D.palettePinned", pinned ? "0" : "1");
      } catch {
        // Nur eine Bequemlichkeit.
      }
      if (pinned) setPaletteHover(false);
      return !pinned;
    });
  };
  const PALETTE_EDGE_PX = 32;

  // Vollbild: der ganze Inhalt des Reiters über dem Portal; Esc verlässt es (außer beim Zeichnen, dort bricht Esc ab).
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => mapRef.current?.resize?.(), 50);
    if (!fullscreen) return () => window.clearTimeout(id);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !massingMode) setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreen, massingMode]);
  const buildingId = String(properties.building._id);

  useEffect(() => {
    let cancelled = false;
    setMassingBodies([]);
    setMassingSaved("[]");
    setMassingContext(null);
    userAPI
      .getBuildingMassing(buildingId)
      .then((result: any) => {
        if (cancelled) return;
        const bodies = Array.isArray(result?.bodies) ? result.bodies : [];
        setMassingBodies(bodies);
        setMassingSaved(JSON.stringify(bodies));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [buildingId]);

  // Der Rahmen (Flurstück, zulässiges Maß) kostet den Server einige Sekunden: erst, wenn gezeichnet wird oder Baukörper da sind.
  const [openSpaceOn, setOpenSpaceOn] = useState(false);
  const [setbacksOn, setSetbacksOn] = useState(false);
  const needsMassingContext = massingMode !== null || massingBodies.length > 0 || openSpaceOn || setbacksOn;
  // Der Rahmen gilt für das Grundstück unter dem Baukörper (dem ausgewählten, sonst dem ersten); ohne Baukörper für
  // das Gebäude. Neu geladen wird erst, wenn der Baukörper das Flurstück verlässt.
  const anchorBody = massingBodies.find((b) => b.id === massingSelected) ?? massingBodies[0] ?? null;
  const anchor = anchorBody && anchorBody.ring.length >= 3 ? centroidOf(anchorBody.ring) : null;
  const anchorOutside =
    !!anchor && !!massingContext && (!massingContext.parcel?.geometry || !booleanPointInPolygon(anchor, massingContext.parcel.geometry));
  useEffect(() => {
    if (anchorOutside && !massingContextLoading) setMassingContext(null);
  }, [anchorOutside, massingContextLoading]);
  useEffect(() => {
    if (!needsMassingContext || massingContext || massingContextLoading) return;
    const point = anchor ? { lat: anchor[1], lon: anchor[0] } : null;
    setMassingContextLoading(true);
    userAPI
      .getBuildingMassingContext(buildingId, point)
      .then((result: any) =>
        setMassingContext({
          parcel: result?.parcel ?? null,
          allowed: result?.allowed ?? null,
          planFeatures: Array.isArray(result?.planFeatures) ? result.planFeatures : [],
          openSpaceMin: result?.openSpaceMin ?? null,
        }),
      )
      .catch(() => setMassingContext({ parcel: null, allowed: null }))
      .finally(() => setMassingContextLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsMassingContext, massingContext, massingContextLoading, buildingId]);

  const addMassingBody = (ring: LonLat[]) => {
    const body: MassingBody = {
      id: newBodyId(),
      name: `${t("building3D.massing.body")} ${massingBodies.length + 1}`,
      ring,
      storeys: massingContext?.allowed?.storeys?.value ?? DEFAULT_STOREYS,
      storeyHeightM: DEFAULT_STOREY_HEIGHT_M,
    };
    setMassingBodies((current) => [...current, body]);
    setMassingSelected(body.id);
    setMassingPoints([]);
    setMassingHover(null);
    setMassingMode(null);
  };

  const finishMassingPolygon = () => {
    if (massingPointsRef.current.length >= 3) addMassingBody(massingPointsRef.current);
  };

  // Klicks auf die Karte setzen Punkte, solange ein Zeichenmodus aktiv ist; Esc bricht ab.
  useEffect(() => {
    const map = mapRef.current;
    if (!sceneReady || !map || !massingMode) return;
    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = "crosshair";
    map.doubleClickZoom.disable();
    // Beim Polygon rastet die neue Kante rechtwinklig oder in Verlängerung zur vorigen ein (abschaltbar, Shift hält frei).
    const snapped = (event: any): { point: LonLat; snapped: boolean } => {
      const raw: LonLat = [event.lngLat.lng, event.lngLat.lat];
      const points = massingPointsRef.current;
      if (massingMode !== "polygon" || !snapRef.current || event.originalEvent?.shiftKey || points.length < 2) return { point: raw, snapped: false };
      return snapPolygonPoint(points, raw, 12);
    };
    const onClick = (event: any) => {
      const point = snapped(event).point;
      const points = massingPointsRef.current;
      if (massingMode === "rectangle") {
        if (points.length === 2) addMassingBody(rectangleFrom(points[0], points[1], point));
        else setMassingPoints([...points, point]);
        return;
      }
      // Polygon: ein Klick nahe am ersten Punkt schließt den Grundriss.
      if (points.length >= 3) {
        const first = map.project(points[0]);
        if (Math.hypot(first.x - event.point.x, first.y - event.point.y) < 10) {
          addMassingBody(points);
          return;
        }
      }
      setMassingPoints([...points, point]);
    };
    const onMove = (event: any) => {
      const result = snapped(event);
      setMassingHover(result.point);
      setMassingSnapped(result.snapped);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMassingMode(null);
        setMassingPoints([]);
        setMassingHover(null);
      }
      if (event.key === "Enter" && massingMode === "polygon") finishMassingPolygon();
    };
    map.on("click", onClick);
    map.on("mousemove", onMove);
    window.addEventListener("keydown", onKey);
    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMove);
      window.removeEventListener("keydown", onKey);
      canvas.style.cursor = previousCursor;
      map.doubleClickZoom.enable();
    };
    // addMassingBody liest den jüngsten Zustand über die Setter; neu binden nur bei neuem Modus oder neuer Karte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneReady, massingMode]);

  // Gezeichnete Baukörper wie eigene Modelle bedienen: Klick wählt, Ziehen verschiebt, Alt + Ziehen dreht.
  const massingBodiesRef = useRef<MassingBody[]>([]);
  massingBodiesRef.current = massingBodies;
  const massingSelectedRef = useRef<string | null>(null);
  massingSelectedRef.current = massingSelected;
  useEffect(() => {
    const map = mapRef.current;
    if (!sceneReady || !map || massingMode) return;
    // Die Ebenen entstehen erst, wenn die Szene steht — bei jedem Klick neu nachsehen. Verdeckt die 3D-Szene den
    // Treffer, zählt der Grundriss unter dem Mauszeiger.
    const bodyAt = (point: any): string | null => {
      const layers = ["massing-storeys", "massing-potential"].filter((id) => map.getLayer(id));
      const hit = layers.length ? map.queryRenderedFeatures(point, { layers })[0] : null;
      if (hit?.properties?.id) return hit.properties.id;
      const ground = map.unproject(point);
      const under = massingBodiesRef.current.find(
        (b) => b.ring.length >= 3 && booleanPointInPolygon([ground.lng, ground.lat], { type: "Polygon", coordinates: [closed(b.ring)] } as any),
      );
      return under?.id ?? null;
    };
    let drag: { id: string; start: any; ring: LonLat[]; rotate: boolean; startAngle: number } | null = null;
    const angleTo = (centre: LonLat, lngLat: any) => (Math.atan2(lngLat.lat - centre[1], (lngLat.lng - centre[0]) * Math.cos((centre[1] * Math.PI) / 180)) * 180) / Math.PI;
    const onClick = (event: any) => {
      const id = bodyAt(event.point);
      if (id) setMassingSelected(id);
    };
    const onDown = (event: any) => {
      const id = bodyAt(event.point);
      if (!id || id !== massingSelectedRef.current) return;
      const body = massingBodiesRef.current.find((b) => b.id === id);
      if (!body) return;
      event.preventDefault();
      map.dragPan.disable();
      const rotate = !!event.originalEvent?.altKey;
      const centre = body.ring.reduce((c, p) => [c[0] + p[0] / body.ring.length, c[1] + p[1] / body.ring.length], [0, 0]) as LonLat;
      drag = { id, start: event.lngLat, ring: body.ring, rotate, startAngle: angleTo(centre, event.lngLat) };
      map.getCanvas().style.cursor = rotate ? "alias" : "grabbing";
    };
    const onMove = (event: any) => {
      if (!drag) {
        map.getCanvas().style.cursor = bodyAt(event.point) ? "pointer" : "";
        return;
      }
      const d = drag;
      let ring: LonLat[];
      if (d.rotate) {
        const centre = d.ring.reduce((c, p) => [c[0] + p[0] / d.ring.length, c[1] + p[1] / d.ring.length], [0, 0]) as LonLat;
        ring = rotateRing(d.ring, angleTo(centre, event.lngLat) - d.startAngle);
      } else {
        const dLng = event.lngLat.lng - d.start.lng;
        const dLat = event.lngLat.lat - d.start.lat;
        ring = d.ring.map((p) => [p[0] + dLng, p[1] + dLat] as LonLat);
      }
      setMassingBodies((current) => current.map((b) => (b.id === d.id ? { ...b, ring } : b)));
    };
    const onUp = () => {
      if (!drag) return;
      drag = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = "";
    };
    map.on("click", onClick);
    map.on("mousedown", onDown);
    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    return () => {
      map.off("click", onClick);
      map.off("mousedown", onDown);
      map.off("mousemove", onMove);
      map.off("mouseup", onUp);
      map.dragPan.enable();
    };
  }, [sceneReady, massingMode]);

  // Freifläche: Flurstück ohne Bestand (Bodenflächen der 3D-Gebäude) und ohne gezeichnete Baukörper.
  const openSpace = useMemo(() => {
    if (!openSpaceOn || !massingContext?.parcel?.geometry) return null;
    return openSpaceOf(massingContext.parcel.geometry, [...footprintsFromSurfaces(buildings ?? []), ...massingBodies.map((b) => b.ring)]);
  }, [openSpaceOn, massingContext, buildings, massingBodies]);

  // Abstandsflächen je Baukörper: Tiefe aus Wandhöhe und Landesbauordnung, gestrichelt; was über das Grundstück ragt, rot.
  const setbacks = useMemo(() => {
    if (!setbacksOn) return [];
    const rule = setbackRuleFor(massingContext?.parcel?.landSchl ?? massingContext?.parcel?.id, massingContext?.allowed?.uses ?? []);
    return massingBodies.map((b) => {
      const heightM = b.storeys * b.storeyHeightM;
      const depthM = setbackDepthM(heightM, rule);
      const { union } = setbackOf(b.ring, depthM);
      return { id: b.id, name: b.name, heightM, depthM, rule, union, outside: setbackOutside(union, massingContext?.parcel?.geometry ?? null) };
    });
  }, [setbacksOn, massingBodies, massingContext]);

  const nudgeMassingBody = (east: number, north: number) =>
    setMassingBodies((current) => current.map((b) => (b.id === massingSelected ? { ...b, ring: translateRing(b.ring, east, north) } : b)));
  const rotateMassingBody = (degrees: number) =>
    setMassingBodies((current) => current.map((b) => (b.id === massingSelected ? { ...b, ring: rotateRing(b.ring, degrees) } : b)));

  // Baukörper, Entwurf und Flurstück auf die Karte — nach jedem Neuaufbau der Karte erneut.
  useEffect(() => {
    const map = mapRef.current;
    if (!sceneReady || !map) return;
    const preview =
      massingMode === "rectangle" && massingPoints.length === 2 && massingHover
        ? rectangleFrom(massingPoints[0], massingPoints[1], massingHover)
        : massingHover && massingMode
          ? [...massingPoints, massingHover]
          : massingPoints;
    // Das Polygon wird immer geschlossen gezeigt, mit der Länge der schließenden Kante; jede rechtwinklige Ecke
    // bekommt „90°“, die Linie wird kräftig, solange der Punkt unter der Maus eingerastet ist.
    const closedPreview = (massingMode === "rectangle" && preview.length === 4) || (massingMode === "polygon" && preview.length >= 3);
    const draft = draftGeoJson(preview, closedPreview);
    const snapNow = massingSnapped && massingMode === "polygon" && massingPoints.length >= 2 && !!massingHover;
    draft.line.features.forEach((f: any) => (f.properties.snapped = snapNow));
    if (massingMode) {
      for (const corner of rightAngleCorners(preview, closedPreview)) {
        draft.labels.features.push({ type: "Feature" as const, geometry: { type: "Point" as const, coordinates: corner }, properties: { label: "90°", corner: true } as any });
      }
    }
    const plot = massingContext?.parcel?.geometry
      ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: massingContext.parcel.geometry, properties: {} }] }
      : { type: "FeatureCollection", features: [] };
    const sources: Record<string, any> = {
      "massing-bodies": bodiesGeoJson(massingBodies, massingSelected, massingContext?.allowed?.storeys?.value ?? null),
      "massing-outlines": outlinesGeoJson(massingBodies, massingSelected),
      "massing-selected-vertices": {
        type: "FeatureCollection",
        features: (massingBodies.find((b) => b.id === massingSelected)?.ring ?? []).map((p) => ({ type: "Feature", geometry: { type: "Point", coordinates: p }, properties: {} })),
      },
      "massing-draft-line": draft.line,
      "massing-draft-points": draft.points,
      "massing-draft-labels": draft.labels,
      "massing-plot": plot,
      "open-space": openSpace?.geometry
        ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: openSpace.geometry, properties: {} }] }
        : { type: "FeatureCollection", features: [] },
      "setback-areas": {
        type: "FeatureCollection",
        features: setbacks.filter((x) => x.union).map((x) => ({ type: "Feature", geometry: x.union.geometry, properties: { id: x.id } })),
      },
      "setback-outside": {
        type: "FeatureCollection",
        features: setbacks.filter((x) => x.outside?.geometry).map((x) => ({ type: "Feature", geometry: x.outside!.geometry, properties: { id: x.id } })),
      },
      "plan-features": {
        type: "FeatureCollection",
        features: openSpaceOn ? (massingContext?.planFeatures ?? []).map((f) => ({ type: "Feature", geometry: f.geometry, properties: { kind: f.kind, label: f.label } })) : [],
      },
    };
    for (const [id, data] of Object.entries(sources)) {
      const source = map.getSource(id);
      if (source) source.setData(data);
      else map.addSource(id, { type: "geojson", data });
    }
    // Jede Ebene einzeln: eine Karte, die schon ältere Ebenen trägt, bekommt die fehlenden dazu.
    const below = map.getLayer("lod2-buildings") ? "lod2-buildings" : undefined;
    const ensure = (layer: any, beforeId?: string) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, beforeId);
    };
    // Am Boden, unter den Gebäuden: Freifläche, Grün- und Pflanzfestsetzungen, Baufenster, Flurstück.
    ensure({ id: "open-space", type: "fill", source: "open-space", paint: { "fill-color": "#6DBE45", "fill-opacity": 0.5 } }, below);
    ensure(
      {
        id: "plan-green",
        type: "fill",
        source: "plan-features",
        filter: ["in", ["get", "kind"], ["literal", ["gruen", "pflanzung", "frei"]]],
        paint: { "fill-color": ["case", ["==", ["get", "kind"], "pflanzung"], "#2E8B3E", "#4FA65B"], "fill-opacity": 0.45 },
      },
      below,
    );
    ensure(
      {
        id: "plan-lines",
        type: "line",
        source: "plan-features",
        filter: ["in", ["get", "kind"], ["literal", ["baufenster", "baugrenze"]]],
        paint: { "line-color": "#2B6CB0", "line-width": ["case", ["==", ["get", "kind"], "baugrenze"], 2.5, 1.5], "line-dasharray": [3, 2] },
      },
      below,
    );
    ensure({ id: "massing-plot", type: "line", source: "massing-plot", paint: { "line-color": "#E0A800", "line-width": 2.5 } }, below);
    ensure({ id: "setback-fill", type: "fill", source: "setback-areas", paint: { "fill-color": "#5B6B7A", "fill-opacity": 0.08 } }, below);
    ensure({ id: "setback-line", type: "line", source: "setback-areas", paint: { "line-color": "#3F4A55", "line-width": 1.6, "line-dasharray": [2, 2] } }, below);
    ensure({ id: "setback-outside", type: "fill", source: "setback-outside", paint: { "fill-color": "#D64545", "fill-opacity": 0.45 } }, below);
    // Wie die 3D-Gebäude ringsum, aber hell: Wände fast weiß, Geschossbänder in hellem Terrakotta — ein Sandwich.
    // Schwaches Licht, damit die Fassaden nicht ins Dunkle kippen.
    map.setLight({ anchor: "viewport", position: [1.15, 210, 30], color: "#ffffff", intensity: 0.15 });
    ensure({ id: "massing-outlines", type: "line", source: "massing-outlines", filter: ["!=", ["get", "selected"], true], paint: { "line-color": "#C98B74", "line-width": 1.5 } });
    ensure({
      id: "massing-storeys",
      type: "fill-extrusion",
      source: "massing-bodies",
      filter: ["in", ["get", "kind"], ["literal", ["storey", "slab"]]],
      paint: {
        "fill-extrusion-color": ["case", ["==", ["get", "kind"], "slab"], "#C98B74", ["case", ["get", "selected"], "#FFF1CC", "#F4F1EA"]],
        "fill-extrusion-base": ["get", "base"],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-opacity": 1,
        "fill-extrusion-vertical-gradient": false,
      },
    });
    // Was das zulässige Maß darüber noch erlaubt: dieselben Geschosse, durchscheinend.
    ensure({
      id: "massing-potential",
      type: "fill-extrusion",
      source: "massing-bodies",
      filter: ["in", ["get", "kind"], ["literal", ["potential", "potentialSlab"]]],
      paint: {
        "fill-extrusion-color": ["case", ["==", ["get", "kind"], "potentialSlab"], "#C98B74", "#F4F1EA"],
        "fill-extrusion-base": ["get", "base"],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-opacity": 0.35,
        "fill-extrusion-vertical-gradient": false,
      },
    });
    // Die Auswahl, deutlich: kräftiger Umriss am Boden und die Eckpunkte.
    ensure({ id: "massing-selected-outline", type: "line", source: "massing-outlines", filter: ["==", ["get", "selected"], true], paint: { "line-color": "#E8890C", "line-width": 4 } });
    ensure({
      id: "massing-selected-vertices",
      type: "circle",
      source: "massing-selected-vertices",
      paint: { "circle-radius": 5, "circle-color": "#ffffff", "circle-stroke-color": "#E8890C", "circle-stroke-width": 2.5 },
    });
    ensure({ id: "massing-draft-line", type: "line", source: "massing-draft-line", paint: { "line-color": ["case", ["get", "snapped"], "#E8890C", "#1A1A1A"], "line-width": ["case", ["get", "snapped"], 3, 2] } });
    ensure({
      id: "massing-draft-points",
      type: "circle",
      source: "massing-draft-points",
      paint: { "circle-radius": ["case", ["get", "first"], 6, 4], "circle-color": "#F2C14E", "circle-stroke-color": "#1A1A1A", "circle-stroke-width": 1.5 },
    });
    ensure({
      id: "massing-draft-labels",
      type: "symbol",
      source: "massing-draft-labels",
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-allow-overlap": true,
        "text-offset": ["case", ["==", ["get", "corner"], true], ["literal", [0, -1.3]], ["literal", [0, 0]]],
      },
      paint: { "text-color": "#ffffff", "text-halo-color": "#1A1A1A", "text-halo-width": 3 },
    });
    map.triggerRepaint?.();
  }, [sceneReady, massingBodies, massingSelected, massingMode, massingPoints, massingHover, massingSnapped, massingContext, openSpace, openSpaceOn, setbacks]);

  const saveMassing = async () => {
    setMassingSaving(true);
    setMassingError(null);
    try {
      const result: any = await userAPI.saveBuildingMassing(buildingId, massingBodies);
      const bodies = Array.isArray(result?.bodies) ? result.bodies : massingBodies;
      setMassingBodies(bodies);
      setMassingSaved(JSON.stringify(bodies));
    } catch (error: any) {
      setMassingError(error?.message ?? String(error));
    } finally {
      setMassingSaving(false);
    }
  };

  const toggleParcels = (on: boolean) => {
    setParcelsOn(on);
    try {
      window.localStorage.setItem(PARCELS_KEY, on ? "1" : "0");
    } catch {
      // Nur eine Bequemlichkeit; ohne Speicher wird der Schalter nicht gemerkt.
    }
  };

  const toggleShadows = (on: boolean) => {
    setShadows(on);
    try {
      window.localStorage.setItem(SHADOWS_KEY, on ? "1" : "0");
    } catch {
      // A viewer convenience only; without storage the toggle simply is not remembered.
    }
  };
  const sunBelowHorizon = (() => {
    const threebox = threeboxRef.current;
    if (!shadows || !sceneReady || !threebox?.getSunPosition || !centre) return false;
    return threebox.getSunPosition(sunDate, [centre.lon, centre.lat]).altitude <= 0;
  })();

  // Own models of this building: listed on change, the newest one is shown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result: any = await userAPI.getBuildingModels(String(properties.building._id));
        if (!cancelled) setModels(Array.isArray(result?.models) ? result.models : []);
      } catch {
        if (!cancelled) setModels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [properties.building._id]);

  useEffect(() => {
    setDraft(activeModel?.placement ?? null);
    setColourMode(activeModel?.colourMode ?? "original");
    setSavedPlacement(activeModel?.placement ?? null);
  }, [activeModel?._id]);

  // The GLB through the authenticated content route, handed to the loader as a blob URL — the
  // loader itself sends no auth header.
  useEffect(() => {
    if (!activeModel?.glbFileId || !authentication?.customer) {
      setModelUrl(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    (async () => {
      const result: any = await userAPI.getManagedFileContent(authentication.customer, activeModel.glbFileId!);
      if (cancelled) return;
      if (!result?.data) {
        setModelError(t("building3D.modelLoadFailed"));
        return;
      }
      url = URL.createObjectURL(new Blob([result.data], { type: "model/gltf-binary" }));
      setModelUrl(url);
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [activeModel?.glbFileId, authentication?.customer]);

  // Into the running scene. glTF is Y-up, the map Z-up: the model is turned once on load, and its
  // own origin (ground, and the IFC origin or the box centre) is the anchor.
  useEffect(() => {
    const threebox = threeboxRef.current;
    if (!sceneReady || !threebox || !modelUrl || !draft) return;
    let removed = false;
    threebox.loadObj(
      { obj: modelUrl, type: "gltf", units: "meters", rotation: { x: 90, y: 0, z: 0 }, anchor: "auto" },
      (model: any) => {
        if (removed) return;
        model.setCoords([draft.lon, draft.lat, draft.altitude]);
        model.setRotation({ x: 0, y: 0, z: threeboxDegrees(draft.rotationDeg) });
        model.traverse((child: any) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        // Colours: each material carries part class, IFC material and original colour (server
        // extras); a GLB without them keeps its own colours in every mode.
        setColourModesAvailable(hasPartClasses(model));
        applyAppearance(model, colourModeRef.current, THREE);
        threebox.add(model);
        modelObjectRef.current = model;
        // Click the model, then drag with Shift (move), Alt (rotate) or Ctrl (height). The result
        // becomes the draft, like a nudge button — saved only with "Platzierung speichern".
        model.addEventListener("ObjectDragged", (event: any) => {
          const action = event?.detail?.draggedAction;
          const [lon, lat, altitude] = model.coordinates ?? [];
          // Take over only what was dragged. Threebox's translate also sets the height — to the
          // model's own height, so a moved model would float by that much.
          setDraft((current) => {
            if (!current) return current;
            if (action === "translate" && Number.isFinite(lon) && Number.isFinite(lat)) return { ...current, lon, lat };
            if (action === "altitude" && Number.isFinite(altitude)) return { ...current, altitude: Math.round(altitude * 100) / 100 };
            if (action === "rotate") return { ...current, rotationDeg: Math.round((((model.rotation?.z ?? 0) * 180) / Math.PI) * 10) / 10 };
            return current;
          });
        });
        mapRef.current?.triggerRepaint?.();
      },
    );
    return () => {
      removed = true;
      if (modelObjectRef.current) threebox.remove?.(modelObjectRef.current);
      modelObjectRef.current = null;
    };
    // The placement is applied by the next effect; reloading the model on every nudge is not needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneReady, modelUrl]);

  useEffect(() => {
    const model = modelObjectRef.current;
    if (!model || !draft) return;
    model.setCoords([draft.lon, draft.lat, draft.altitude]);
    model.setRotation({ x: 0, y: 0, z: threeboxDegrees(draft.rotationDeg) });
    mapRef.current?.triggerRepaint?.();
  }, [draft]);

  useEffect(() => {
    const model = modelObjectRef.current;
    if (!model) return;
    applyAppearance(model, colourMode, THREE);
    mapRef.current?.triggerRepaint?.();
  }, [colourMode]);

  const changeColourMode = async (mode: ColourMode) => {
    const previousMode = colourMode;
    setColourMode(mode);
    if (!activeModel) return;
    try {
      const updated: any = await userAPI.setBuildingModelColourMode(String(properties.building._id), activeModel._id, mode);
      setModels((currentModels) => currentModels.map((m) => (m._id === activeModel._id ? { ...m, colourMode: updated?.colourMode ?? mode } : m)));
    } catch (error: any) {
      setColourMode(previousMode);
      setModelError(t("building3D.modelFailed", { error: error?.message || "" }));
    }
  };

  const uploadModel = async (file: File) => {
    setModelBusy(true);
    setModelError(null);
    try {
      const created: any = await userAPI.uploadBuildingModel(String(properties.building._id), file);
      if (created?._id) setModels([created, ...models]);
    } catch (error: any) {
      setModelError(t("building3D.modelFailed", { error: error?.message || "" }));
    } finally {
      setModelBusy(false);
    }
  };

  const savePlacement = async () => {
    if (!activeModel || !draft) return;
    setModelBusy(true);
    try {
      const updated: any = await userAPI.updateBuildingModelPlacement(String(properties.building._id), activeModel._id, draft);
      setSavedPlacement(updated?.placement ?? draft);
      setModels(models.map((m) => (m._id === activeModel._id ? { ...m, placement: updated?.placement ?? draft } : m)));
    } catch (error: any) {
      setModelError(t("building3D.modelFailed", { error: error?.message || "" }));
    } finally {
      setModelBusy(false);
    }
  };

  const removeModel = async () => {
    if (!activeModel) return;
    setModelBusy(true);
    try {
      await userAPI.deleteBuildingModel(String(properties.building._id), activeModel._id);
      setModels(models.slice(1));
    } catch (error: any) {
      setModelError(t("building3D.modelFailed", { error: error?.message || "" }));
    } finally {
      setModelBusy(false);
    }
  };

  const unsaved = !!draft && !!savedPlacement && JSON.stringify(draft) !== JSON.stringify(savedPlacement);

  /**
   * Vegetation: read the stored state when the building changes; while an analysis runs, ask again
   * until it is done. Starting it is the button's job — it takes minutes and is never automatic.
   */
  useEffect(() => {
    if (!centre) return;
    let cancelled = false;
    let timer: any = null;
    const load = async () => {
      try {
        const result: any = await userAPI.getVegetation(centre.lat, centre.lon, VEGETATION_RADIUS);
        if (cancelled) return;
        setVegetation(result);
        if (result?.status === "pending") timer = setTimeout(load, VEGETATION_POLL_MS);
      } catch {
        if (!cancelled) setVegetation(null);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [centre, vegetation?.status === "pending"]);

  const startVegetation = async () => {
    if (!centre) return;
    setVegetationStarting(true);
    try {
      const result: any = await userAPI.startVegetation(
        centre.lat,
        centre.lon,
        VEGETATION_RADIUS,
        properties.building?.postalCode ? String(properties.building.postalCode) : undefined,
      );
      setVegetation(result);
    } catch {
      setVegetation({ status: "failed" });
    } finally {
      setVegetationStarting(false);
    }
  };

  // Into the running scene, once both are there; replaced when the result changes.
  useEffect(() => {
    const threebox = threeboxRef.current;
    if (!sceneReady || !threebox || !centre || vegetation?.status !== "ready") return;
    const group = buildVegetationGroup(
      vegetation.items ?? [],
      vegetation.lawn ?? [],
      projectToWorld,
      unitsPerMetreAt(centre.lat),
    );
    threebox.world.add(group);
    mapRef.current?.triggerRepaint?.();
    return () => {
      threebox.world?.remove(group);
      mapRef.current?.triggerRepaint?.();
    };
  }, [sceneReady, vegetation, centre]);

  const treeCount = (vegetation?.items ?? []).filter((item) => item.type === "tree").length;
  const shrubCount = (vegetation?.items ?? []).filter((item) => item.type === "shrub").length;

  if (!centre) {
    return <div style={{ color: "#767676" }}>{t("building3D.noLocation")}</div>;
  }

  // Dieselben Werte für die Palette an der Karte und für den Teil darunter.
  const controlProps = {
    vegetation: vegetation,
    treeCount: treeCount,
    shrubCount: shrubCount,
    vegetationStarting: vegetationStarting,
    onStartVegetation: startVegetation,
    parcels: parcelsOn,
    onParcels: toggleParcels,
    parcelsLoading: parcelsLoading,
    parcelCount: parcels?.features.length ?? null,
    shadows: shadows,
    onShadows: toggleShadows,
    sunDate: sunDate,
    onSunDate: setSunDate,
    sunBelowHorizon: sunBelowHorizon,
    model: activeModel,
    draft: draft,
    unsaved: unsaved,
    modelBusy: modelBusy,
    modelError: modelError,
    colourMode: colourMode,
    colourModesAvailable: colourModesAvailable,
    onColourMode: changeColourMode,
    onNudge: (east: number, north: number) => draft && setDraft(nudge(draft, east, north)),
    onRotate: (delta: number) => draft && setDraft({ ...draft, rotationDeg: draft.rotationDeg + delta }),
    onLift: (delta: number) => draft && setDraft({ ...draft, altitude: draft.altitude + delta }),
    onSave: savePlacement,
    onRemove: removeModel,
    onUpload: () => fileInputRef.current?.click(),
    drawMode: massingMode,
    onDrawMode: (mode: DrawMode) => {
      setMassingMode(mode);
      setMassingPoints([]);
      setMassingHover(null);
    },
    drawPoints: massingPoints.length,
    onDrawFinish: finishMassingPolygon,
    snapRightAngles,
    onSnapRightAngles: setSnapRightAngles,
    openSpace: openSpaceOn,
    onOpenSpace: setOpenSpaceOn,
    setbacks: setbacksOn,
    onSetbacks: setSetbacksOn,
    openSpaceLoading: openSpaceOn && massingContextLoading,
    openSpaceInfo:
      !openSpaceOn || massingContextLoading
        ? null
        : !massingContext?.parcel
          ? t("building3D.massing.noParcel")
          : openSpace
            ? t("building3D.ui.openSpaceArea", { m2: Math.round(openSpace.areaM2).toLocaleString("de-DE"), share: Math.round((openSpace.areaM2 / massingContext.parcel.areaM2) * 100) })
            : null,
    selectedBodyName: massingBodies.find((b) => b.id === massingSelected)?.name ?? null,
    onBodyNudge: nudgeMassingBody,
    onBodyRotate: rotateMassingBody,
  };

  return (
    <div className={fullscreen ? "fixed inset-0 z-50 overflow-y-auto bg-background p-4" : undefined}>
      {busy ? <div style={{ color: "#767676" }}>{t("building3D.loading")}</div> : null}
      {failed ? (
        <div style={{ color: "#a4262c" }}>{t("building3D.failed")}</div>
      ) : null}
      {!busy && !failed && buildings && buildings.length === 0 ? (
        <div
          className="paddingM"
          style={{ border: "1px solid #eee", borderRadius: "4px", color: "#8a6d1f" }}
        >
          {t("building3D.noData")}
        </div>
      ) : null}
      {buildings && buildings.length > 0 ? (
        <>
          <div
            className="relative overflow-hidden rounded"
            style={{ height: fullscreen ? "calc(100vh - 7rem)" : "600px" }}
            onMouseMove={(event) => {
              if (palettePinned) return;
              const box = event.currentTarget.getBoundingClientRect();
              if (event.clientX > box.right - PALETTE_EDGE_PX) setPaletteHover(true);
            }}
            onMouseLeave={() => setPaletteHover(false)}
          >
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%", borderRadius: "4px" }} />
            <button
              type="button"
              onClick={() => setFullscreen((on) => !on)}
              aria-label={t(fullscreen ? "building3D.ui.fullscreenExit" : "building3D.ui.fullscreen")}
              title={t(fullscreen ? "building3D.ui.fullscreenExit" : "building3D.ui.fullscreen")}
              className="absolute left-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-md border bg-background/95 shadow-sm hover:bg-accent"
            >
              {fullscreen ? <Minimize2 aria-hidden className="h-4 w-4" /> : <Maximize2 aria-hidden className="h-4 w-4" />}
            </button>
            {/* Griff am rechten Rand: zeigt, dass dort die Werkzeuge liegen. */}
            {!paletteOpen ? (
              <div aria-hidden className="pointer-events-none absolute right-0 top-1/2 z-10 h-16 w-1.5 -translate-y-1/2 rounded-l bg-foreground/30" />
            ) : null}
            {/* Werkzeugpalette: Zeichnen, Umgebung, Licht — fliegt am rechten Rand ein. */}
            <aside
              aria-hidden={!paletteOpen}
              onMouseLeave={() => setPaletteHover(false)}
              className={
                "absolute bottom-7 right-0 top-0 z-10 w-[19rem] overflow-y-auto rounded-bl-md border-b border-l bg-background/95 p-3 shadow-lg backdrop-blur transition-transform duration-300 " +
                (paletteOpen ? "translate-x-0" : "pointer-events-none translate-x-full")
              }
            >
              <div className="mb-2 flex justify-end">
                <button
                  type="button"
                  onClick={togglePalettePin}
                  aria-pressed={palettePinned}
                  title={t(palettePinned ? "building3D.ui.paletteUnpin" : "building3D.ui.palettePin")}
                  className={"flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent " + (palettePinned ? "text-foreground" : "text-muted-foreground")}
                >
                  {palettePinned ? <PinOff aria-hidden className="h-4 w-4" /> : <Pin aria-hidden className="h-4 w-4" />}
                </button>
              </div>
              <Building3DControls part="palette" {...controlProps} />
            </aside>
          </div>
          {status ? (
            <div className="marginTopS" style={{ fontSize: "11px", color: "#666" }}>
              {status.truncated
                ? t("building3D.truncated", {
                    rendered: status.rendered,
                    total: status.total,
                  })
                : t("building3D.count", { count: status.rendered })}
            </div>
          ) : null}
                      <input
              ref={fileInputRef}
              type="file"
              accept=".ifc,.glb"
              style={{ display: "none" }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) uploadModel(file);
              }}
            />
          <Building3DControls part="bottom" {...controlProps} />
          <MassingPanel
            context={massingContext}
            contextLoading={massingContextLoading}
            bodies={massingBodies}
            selectedId={massingSelected}
            onSelect={setMassingSelected}
            onChange={(body) => setMassingBodies((current) => current.map((b) => (b.id === body.id ? body : b)))}
            onRemove={(id) => setMassingBodies((current) => current.filter((b) => b.id !== id))}
            dirty={JSON.stringify(massingBodies) !== massingSaved}
            saving={massingSaving}
            error={massingError}
            onSave={saveMassing}
            openSpace={openSpace}
            setbacks={setbacks.map((x) => ({ name: x.name, depthM: x.depthM, heightM: x.heightM, factor: x.rule.factor, minM: x.rule.minM, law: x.rule.law, outsideM2: x.outside?.areaM2 ?? 0 }))}
          />
        </>
      ) : null}
    </div>
  );
}
